import { classify } from 'sidecar-utilities'

// modalPaddingX is used by header and body
const modalPaddingX = 'px-5 sm:px-6'

/**
 * The resized height, as an inline `max-height` that still respects the safe
 * area.
 *
 * The resize height has to be inline — it's a live pixel value from a drag,
 * which no class can express. But inline styles outrank classes, so a bare
 * `max-height: {height}px` silently defeated `modalStyles`'
 * `max-h-[calc(100%-var(--safe-top)-theme(spacing.4))]` and let a tall poptart
 * open with its top edge, and its resize handle, under a notch or status bar.
 *
 * Clamping in JS instead can't work: `--safe-top` resolves to a `max(env(…),
 * …)` token rather than a length, and `env()` isn't readable from script at
 * all, so the inset is only knowable to the browser. Folding the same bound
 * into the inline value hands it back to the browser to resolve, and `min()`
 * means whichever constraint is smaller wins — the drag height on a roomy
 * screen, the safe area on a short one.
 *
 * `dvh` rather than `%`: the percentage resolved against `#popTartWrapper`,
 * which has no definite height at or above `xs`, so the class was inert on
 * desktop anyway. `dvh` also shrinks with mobile browser chrome, where `vh`
 * would not.
 *
 * The 1rem spacer is `theme(spacing.4)` written out — `theme()` is a build-time
 * Tailwind function and this string is assembled at runtime, so it has to be
 * the literal the class compiles to. It matches the class above; change both
 * together.
 *
 * Maximized keeps its own `!max-h-full`, which already beats inline.
 */
export const getMaxHeightStyle = ({ height, isMaximized }) =>
  isMaximized
    ? undefined
    : `min(${height}px, calc(100dvh - var(--safe-top) - 1rem))`

export const modalStyles = isMaximized =>
  classify({
    styles: 'color-bgDefaultContainer elevation-3rd-floor focus:outline-none',
    // flex-col is needed to make sure the modal body is full height
    flex: 'flex flex-col',
    maxHeight: !isMaximized
      ? 'h-fit max-h-[calc(100%-var(--safe-top)-theme(spacing.4))]'
      : '!max-h-full',
    width: !isMaximized ? 'w-full min-w-0 max-w-115' : '',
    position: !isMaximized ? 'max-xs:fixed' : 'fixed z-[667]',
    bottom: !isMaximized ? 'bottom-0' : '',
    mobilePosition: !isMaximized ? 'max-xs:mx-auto' : 'max-xs:!inset-0',
    desktopPosition: !isMaximized ? 'xs:right-6' : '!inset-4',
    corners: !isMaximized ? 'rounded-t-xl' : 'xs:rounded-xl',
    animation: 'data-[state=open]:modal-show',
    // The parent #popTartWrapper div has pointer-events-none so you can
    // interact with content below it. The poptart needs to reject its
    // lineage with auto
    cursor: 'pointer-events-auto',
  })

export const headerStyles = ({ isMinimized, isMaximized }) =>
  classify({
    border: 'border-b solid border-gray color-border-weak-default',
    flex: 'flex items-center gap-4',
    modalPaddingX,
    bottomPadding: 'pb-3.5',
    maximizedPadding: isMaximized
      ? 'pt-[calc(var(--safe-top)+theme(spacing.3))]'
      : '',
    minimizedPadding: isMinimized ? 'pt-3.5' : '',
    // expandedPadding is 0 because the resize handle already adds padding
  })

export const resizeAreaStyles = classify({
  // We want padding here to increase the click area (expecially for mobile)
  spacing: 'pt-2 pb-3',
  cursor: 'cursor-ns-resize',
  childHoverEffect: 'group/resizeHandle',
  // Prevent scrolling when touching the resize handle
  touchAction: 'touch-none',
})

export const resizeHandleStyles = classify({
  spacing: 'mx-auto',
  size: 'w-10 h-1',
  color: 'color-inverse-weak-default',
  hover: 'group-hover/resizeHandle:color-inverse-weak-hover',
  active: 'group-active/resizeHandle:color-inverse-weak-active',
  focus: 'group-focus/resizeHandle:color-inverse-weak-active',
  corners: 'rounded-full',
  flex: 'shrink-0',
})

export const titleStyles = 'text-xl md:text-2xl w-full m-0'

export const collapsibleContentStyles = isExpanded =>
  classify({
    flex: 'flex flex-col self-stretch overflow-hidden',
    collapsing: !isExpanded && 'max-h-0',
  })

export const bodyStyles = (isMaximized, isResizing = false) =>
  classify({
    // Off while the member is dragging the handle. Everything this body can
    // contain is a place the drag can be lost, and a cross-origin iframe is the
    // worst of them: Ash fills this box, a drag downward is over it at once, and
    // mouse events inside it belong to its own document — so ours stops seeing the
    // moves and never sees the release, leaving the panel following the bare
    // cursor. `useHeightResize` guards the release a second way; this is what
    // keeps the drag itself smooth.
    dragging: isResizing && 'pointer-events-none',
    // `dvh`, to agree with the `100dvh` the panel above is clamped to (see
    // `getMaxHeightStyle`) rather than the `100vh` that `h-screen` means — the
    // *large* viewport on iOS, the height with the browser toolbar hidden.
    //
    // Intent, not a fix: nothing overflows either way. This box is a shrinkable
    // item in the panel's column flex, and its own `overflow-y-auto` puts its
    // automatic minimum size at zero, so whichever unit is written here it is
    // shrunk to the panel height less the header. The unit only ever decides the
    // flex base size, which is over-tall in both cases. Written the honest way so
    // the next reader isn't left holding two viewport units in one panel and
    // wondering which one the layout follows.
    height: isMaximized ? 'h-dvh' : 'h-[calc(100dvh-theme(spacing.19))]',
    scrollarea: 'self-stretch overflow-y-auto overflow-x-hidden',
    corners: isMaximized ? 'xs:rounded-b-xl' : '',
    modalPaddingX,
    topPadding: 'pt-4',
    // iPhones have a bar at the bottom of their screen that overlaps the app on PWAs.
    // This pushes the content up.
    // See more at https://webkit.org/blog/7929/designing-websites-for-iphone-x/.
    bottomPadding: 'pb-[calc(var(--safe-bottom)+theme(space.4))]',
  })
